'use client';
import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Search } from 'lucide-react';

const COVERAGE_OPTIONS = [
  { label: '₹3L–₹5L', value: '300000-500000' },
  { label: '₹5L–₹10L', value: '500000-1000000' },
  { label: '₹10L–₹50L', value: '1000000-5000000' },
  { label: '₹50L+', value: '5000000-99999999' },
];

const TYPE_OPTIONS = ['Individual', 'Family Floater', 'Senior Citizen', 'Critical Illness'];

export default function QuickFilterBar() {
  const router = useRouter();
  const [coverage, setCoverage] = useState('');
  const [type, setType] = useState('');

  const handleSearch = () => {
    // Backend integration point: pass filters as query params to listing page
    const params = new URLSearchParams();
    if (coverage) params?.set('coverage', coverage);
    if (type) params?.set('type', type);
    router?.push(`/policy-listing-filters?${params?.toString()}`);
  };

  return (
    <section className="py-10">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
        <div className="bg-card border border-border rounded-2xl p-6 card-shadow">
          <div className="text-center mb-6">
            <h2 className="text-xl font-bold text-foreground">Find Your Perfect Health Plan</h2>
            <p className="text-sm text-muted-foreground mt-1">Filter by your needs and compare instantly</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
            {/* Coverage */}
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
                Coverage Amount
              </label>
              <select
                value={coverage}
                onChange={(e) => setCoverage(e?.target?.value)}
                className="w-full px-3 py-2.5 bg-muted border border-border rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">Any coverage</option>
                {COVERAGE_OPTIONS?.map((opt) => (
                  <option key={`cov-${opt?.value}`} value={opt?.value}>{opt?.label}</option>
                ))}
              </select>
            </div>

            {/* Policy Type */}
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
                Policy Type
              </label>
              <select
                value={type}
                onChange={(e) => setType(e?.target?.value)}
                className="w-full px-3 py-2.5 bg-muted border border-border rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">All types</option>
                {TYPE_OPTIONS?.map((t) => (
                  <option key={`type-${t?.toLowerCase()?.replace(/\s/g, '-')}`} value={t}>{t}</option>
                ))}
              </select>
            </div>

            {/* Max Premium */}
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
                Max Annual Premium
              </label>
              <select
                className="w-full px-3 py-2.5 bg-muted border border-border rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">No limit</option>
                <option value="5000">Up to ₹5,000</option>
                <option value="10000">Up to ₹10,000</option>
                <option value="15000">Up to ₹15,000</option>
                <option value="20000">Up to ₹20,000</option>
              </select>
            </div>

            {/* Search button */}
            <button
              onClick={handleSearch}
              className="btn-primary py-2.5 w-full justify-center"
            >
              <Search className="w-4 h-4" />
              Compare Plans
            </button>
          </div>

          <div className="flex items-center justify-center gap-3 mt-4 flex-wrap">
            <span className="text-xs text-muted-foreground">Popular searches:</span>
            {['Family ₹10L cover', 'Senior citizen plan', 'Budget under ₹8K', 'Highest CSR plans']?.map((tag) => (
              <button
                key={`quick-tag-${tag?.toLowerCase()?.replace(/\s/g, '-')}`}
                onClick={() => router?.push('/policy-listing-filters')}
                className="text-xs font-medium px-3 py-1 bg-primary/10 text-primary rounded-full hover:bg-primary/20 transition-colors"
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}